import React, { useCallback, useEffect, useRef, useState } from 'react';

export type ToastType = 'success' | 'error';

export interface UseToastResult {
  showToast: (message: string, type?: ToastType) => void;
  toastElement: React.ReactElement | null;
}

const VISIBLE_MS = 4700;
const REMOVE_MS = 5000;

/**
 * Top-right transient toast. 5s lifecycle: visible 4.7s, slide-out 0.3s, removed at 5s.
 * Pattern mirrors `ForgotPage` toast styling — see `toast toast--verify` global SCSS.
 *
 * Returns a `toastElement` JSX node — render it once near the page root.
 */
const useToast = (): UseToastResult => {
  const [toast, setToast] = useState<string | null>(null);
  const [toastType, setToastType] = useState<ToastType>('success');
  const [toastHiding, setToastHiding] = useState(false);
  const hideRef = useRef<NodeJS.Timeout | null>(null);
  const removeRef = useRef<NodeJS.Timeout | null>(null);

  const showToast = useCallback((message: string, type: ToastType = 'success') => {
    if (hideRef.current) clearTimeout(hideRef.current);
    if (removeRef.current) clearTimeout(removeRef.current);
    setToastHiding(false);
    setToastType(type);
    setToast(message);
    hideRef.current = setTimeout(() => setToastHiding(true), VISIBLE_MS);
    removeRef.current = setTimeout(() => setToast(null), REMOVE_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (hideRef.current) clearTimeout(hideRef.current);
      if (removeRef.current) clearTimeout(removeRef.current);
    };
  }, []);

  const toastElement = toast ? (
    <div
      className={`toast toast--verify toast--${toastType}${toastHiding ? ' toast--hiding' : ''}`}
    >
      {toast}
    </div>
  ) : null;

  return { showToast, toastElement };
};

export default useToast;
